import React, { useEffect, useState } from 'react';
import { UI, Utils } from 'common';
import ContactsCommunication from '../utils/ContactsCommunication';
import Template from '../utils/Template';

const { WrappedDropdownWithLabel, WrappedInputTextWithLabel } = UI;
const { withLayoutAndModalContexts } = Utils;

// We use this component for both select template and save template
const SaveOrSelectTemplate = ({
	isTemplateSaved,
	isTemplateSaveSkipped,
	finishSaveTemplate,
	isSelectTemplatePage,
	mappedTemplateFields,
	savedTemplates,
	modalContext: { showError },
}) => {
	const [selectedTemplate, setSelectedTemplate] = useState(0);
	const [templateName, setTemplateName] = useState('');
	const [templateNameError, setTemplateNameError] = useState(false);
	const [localIsTemplateSaved, setLocalIsTemplateSaved] = useState(isTemplateSaved);
	const [localIsTemplateSaveSkipped, setLocalIsTemplateSaveSkipped] = useState(
		isTemplateSaveSkipped
	);

	const saveTemplate = async (savedTemplate, isSkipped) => {
		let tempMappedTemplateFields = { ...mappedTemplateFields };

		Object.entries(tempMappedTemplateFields).forEach(([columnName, data]) => {
			if (data === '') {
				delete tempMappedTemplateFields[columnName];
			}
		});

		if (isSelectTemplatePage) {
			const savedTemplateResponse = await ContactsCommunication.getSavedTemplate(
				savedTemplate.TemplateId
			);

			if (
				ContactsCommunication.isSuccessful(savedTemplateResponse) &&
				savedTemplateResponse.data
			) {
				tempMappedTemplateFields = savedTemplateResponse.data.Fields;
				finishSaveTemplate(true, {
					TemplateId: savedTemplate.TemplateId,
				});
			} else {
				showError({ savedTemplateResponse });
			}
		} else {
			const requestPayload = Template({
				TemplateId: 0,
				IsTemp: !!isSkipped,
				TemplateName: isSkipped ? null : templateName,
				Fields: tempMappedTemplateFields,
				OverwriteDuplicate: false,
			});

			const response = await ContactsCommunication.saveTemplate(requestPayload);

			if (ContactsCommunication.isSuccessful(response)) {
				finishSaveTemplate(true, response.data);
			} else {
				setTemplateNameError(response.ErrorDescription === 'Template name already exists');
				finishSaveTemplate(false);
				if (response.ErrorDescription !== 'Template name already exists') {
					showError({ response });
				}
			}
		}
	};

	const checkDuplicateTemplateName = () => {
		// check for duplicate name
		const allTemplatesNames = savedTemplates.map(template =>
			template.TemplateName.toLowerCase()
		);

		return allTemplatesNames.includes(templateName.toLowerCase());
	};

	const onMappingTemplateChange = e => {
		setSelectedTemplate(e.value);
	};

	const onInputTextChange = ({ target: { value } }) => {
		setTemplateName(value);
		setTemplateNameError(checkDuplicateTemplateName());
	};

	const onInputTextBlur = ({ target: { value } }) => {
		if (value.trim() === '') {
			setTemplateNameError(true);
		} else {
			setTemplateNameError(checkDuplicateTemplateName());
		}
	};

	useEffect(() => {
		// saves only for save templates UI
		if (isTemplateSaved !== localIsTemplateSaved) {
			const savedTemplate = savedTemplates?.filter(
				template => template.TemplateId === selectedTemplate
			);

			setLocalIsTemplateSaved(isTemplateSaved);

			if (
				(isSelectTemplatePage && savedTemplate && savedTemplate[0]) ||
				(!isSelectTemplatePage &&
					templateName &&
					templateName.trim() !== '' &&
					!templateNameError)
			) {
				saveTemplate(savedTemplate[0]);
			} else {
				setTemplateNameError(true);
			}
		} else if (isTemplateSaveSkipped !== localIsTemplateSaveSkipped) {
			setLocalIsTemplateSaveSkipped(isTemplateSaveSkipped);
			saveTemplate(null, true);
		}
	}, [isTemplateSaved, isTemplateSaveSkipped]);

	return (
		<div className="save-or-select-template">
			<div className="primary-header">
				{isSelectTemplatePage ? 'Select a Template' : 'Save Template'}
			</div>
			<div className="save-template-description">
				{isSelectTemplatePage ? (
					<p>
						Apply a mapping template by selecting it and clicking Continue. To create a
						mapping template, click Skip.
					</p>
				) : (
					`Name and save this mapping template for the next time you import contacts.`
				)}
			</div>
			<p className="field-label">
				<b>{isSelectTemplatePage ? 'Mapping Template:' : 'Template Name:'}</b>
			</p>
			{isSelectTemplatePage ? (
				<WrappedDropdownWithLabel
					className="mapping-dropdown"
					index
					isInputInvalid={templateNameError}
					errorMessage={`${'Select a Template name'}`}
					uiElementConfig={{
						name: 'save-template-dropdown',
						options: savedTemplates,
						optionLabel: 'TemplateName',
						optionValue: 'TemplateId',
						dataqa: 'save-template-dropdown',
						value: selectedTemplate,
						placeholder: 'Select a template',
						onChange: onMappingTemplateChange,
						appendTo: document.body,
					}}
				/>
			) : (
				<WrappedInputTextWithLabel
					className="input-text"
					isInputInvalid={templateNameError}
					errorMessage={`${
						templateName === ''
							? 'Template name is required.'
							: 'Template name already exists'
					}`}
					uiElementConfig={{
						type: 'text',
						value: templateName,
						name: 'templateName',
						dataqa: 'templatename',
						maxLength: 50,
						onChange: onInputTextChange,
						onBlur: onInputTextBlur,
					}}
				/>
			)}
		</div>
	);
};
export default withLayoutAndModalContexts(SaveOrSelectTemplate);
